import './ResultsPage.css';
import { useLocation, useParams } from 'react-router-dom';
import { useEffect, useState, Children } from 'react';
import { Chart } from 'react-chartjs-2';
import { Chart as ChartJS, BarController, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import SideMenu from './SideMenu';
import ReactMarkdown from 'react-markdown';
import PubGraph from './PubGraph';

ChartJS.register(
    BarElement,
    BarController,
    CategoryScale,
    LinearScale,
    Legend,
    Title,
    Tooltip
  );

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function ResultsPage(props) {

    const subMenu = [
        { path: "publications", hu: "Publikációk", en: "Publications"},
        { path: "doctoral", hu: "Doktori iskolák", en: "Doctoral schools"},
        { path: "top", hu: "Top eredmények", en: "Top results"},
        { path: "graph", hu: "Publikációs gráf", en: "Publication graph"},
    ];

    const location = useLocation();
    const param = new URLSearchParams(location.search);
    const lang = param.get("lang") === "en" ? "en" : "hu";

    let { selectedMenu="publications" } = useParams();

    return (
        <div id="results-main" className="page-main">
            <SideMenu menuItems={subMenu} selectedMenuItem={selectedMenu} pathPrefix="/results/" lang={lang} />
            <div className="content-panel">
                <div className="content-main">
                    <h1>{subMenu.find(d => d.path === selectedMenu)[lang]}</h1>
                    {selectedMenu === "publications" && <PublicationResults lang={lang}/>}
                    {selectedMenu === "doctoral" && <PhdResults lang={lang}/>}
                    {selectedMenu === "top" && <TopResults lang={lang}/>}
                    {selectedMenu === "graph" && <PubGraph lang={lang}/>}
                </div>
            </div>            
        </div>        
    );
}

function PublicationResults(props) {

    return <div className="chart-block-container">
        <ChartBlock lang={props.lang} chart="pubs">
            <div className='section-title' lang="hu">Publikációk száma és összetétele</div>
            <div className='section-title' lang="en">Number of publications</div>
            <div lang="hu">            
                A tudományos publikációkat sokféle formában, sokféle forumon lehet közölni. Az alábbiakban a legfontosabb kategóriák statisztikáit közöljük, az <a href="https://m2.mtmt.hu/gui2/?type=institutes&mode=browse&sel=institutes10135">MTMT</a> adatai alapján.
            </div>
            <div lang="en">
                Scientific results can be published in many different forms, in many different forums. Below are the statistics for the most important categories, based on data from <a href="https://m2.mtmt.hu/gui2/?type=institutes&mode=browse&sel=institutes10135">MTMT</a>.
            </div>
        </ChartBlock>
        <ChartBlock lang={props.lang} chart="ranks">
            <div className='section-title' lang="hu">Folyóiratcikkek rangja</div>
            <div className='section-title' lang="en">Rank of journal publications</div>
            <div lang="hu">
                A folyóiratokat szakterületi rangjuk alapján különböző kategóriákba sorolják. A "D1" rangú folyóiratok a szakterületük top 10%-ába tartoznak, a "Q1"-"Q4" kategóriák pedig a rangsor top 25%, a 25%-50%, a 50%-75% és az alsó 25% tartományát jelölik.
                Több szervezet is foglalkozik szakterületi rangsorolással, itt most a <a href="https://www.scimagojr.com/">Scimago</a> rangsor szerinti statisztikákat közöljük. 
            </div>
            <div lang="en">
                Journals are classified into different categories according to their ranks. Journals ranked "D1" are in the top 10% of their field, while the categories "Q1"-"Q4" represent the top 25%, 25%-50%, 50%-75% and bottom 25% quartiles of the ranking.
                There are several organisations that provide rankings by research fields, and here we show statistics according to the <a href="https://www.scimagojr.com/">Scimago</a> ranking. 
            </div>
        </ChartBlock>
        <ChartBlock lang={props.lang} chart="citations">
            <div className='section-title' lang="hu">Idézettség</div>
            <div className='section-title' lang="en">Citations</div>
            <div lang="hu">
                A tudományos eredményesség egyik legfőbb mutatója az idézettség, hiszen hiába a sok rangos publikáció, végső soron az számít, hogy az eredmények mennyire érdekesek a tudományos közösség számára, hányan
                tartják említésre méltónak, gondolják tovább azokat. Természetesen csak a független hivatkozásokat érdemes számba venni, mely nem tartalmazza a szerzők saját munkáinak idézését.
            </div>
            <div lang="en">
                Citation rates are one of the main indicators of scientific output, what ultimately matters is how interesting the results are to the scientific community, how many people consider them worth mentioning. Of course, only independent citations should be counted, which does not include citations of the authors' own work.            
            </div>
        </ChartBlock>
        <ChartBlock lang={props.lang} chart="ifactor">
            <div className='section-title' lang="hu">Impakt faktor</div>
            <div className='section-title' lang="en">Impact factor</div>
            <div lang="hu">
                A publikált cikk rangjának sokat vitatott mérőszáma az impakt faktor. Sokat vitatott, mert az eltérő publikációs szokások miatt az impakt faktorok szakterületenként nagyon eltérőek lehetnek. Ráadásul
                az utóbbi években az impakt faktorok azonos szakterületen is ugrásszerűen megnőttek, így a régebbi és újabb cikkek értékének összehasonlítása is nehezebbé vált. Ezeknek a problémáknak a kiküszöbölésére
                vezették be a normalizált impakt faktort, melyben a normalizálás az adott év adott szakterületének medián impakt faktorával történik.
            </div>
            <div lang="en">
                A questionable measure of the rank of a published paper is the impact factor. It is much debated because, due to different publication habits, impact factors can vary widely from one discipline to another. Moreover, in recent years, impact factors in the same field have also increased by leaps and bounds, making it more difficult to compare the impact factors of older and newer papers. To overcome these problems the normalised impact factor has been introduced, where the normalisation is done by the median impact factor for the given year in the given field.
            </div>
        </ChartBlock>
    </div>;
}

function PhdResults(props) {

    return <div className="chart-block-container">
        <ChartBlock lang={props.lang} chart="phd_total_school">
            <div className='section-title' lang="hu">Doktori iskolák hallgatói létszáma</div>
            <div className='section-title' lang="en">Number of PhD students in the doctoral schools</div>
            <div lang="hu">
                A doktoranduszok rendkívül fontosak a Kar tudományos életében, hiszen létszámuk a teljes oktatói-kutatói állomány több, mint 50%-át teszik ki. A születő publikációk száma, 
                a pályázati és vállalati projekt kapacitás egyaránt közvetlenül összefügg a doktoranduszok számával. A fokozatszerzés után ők jelentik az oktatói bázis utánpótlását, vállalati 
                elhelyezkedés esetén pedig bővítik a Kar céges kapcsolati hálózatát.
            </div>
            <div lang="en">
                PhD students are extremely important in the academic life of the Faculty, as they represent more than 50% of the total teaching and research staff. The number of publications, grant and corporate project capacity is directly related to the number of PhD students. After obtaining their degree, they are the supply of the teaching and research staff, in the case of a corporate career they expand the Faculty's industrial network.
            </div>            
        </ChartBlock>
        <ChartBlock lang={props.lang} chart="phd_total_lang">
            <div className='section-title' lang="hu">Nemzetközi hallgatók</div>
            <div className='section-title' lang="en">International students</div>
            <div lang="hu">
                A Tempus Alapítvány Stipendium Hungaricum programja erős lökést jelentett a doktori iskoláink nemzetköziesedése irányába. Az angol képzésre jelentkező doktoranduszok nagy része a Közel-Keletről, 
                Afrikából, illetve Kínából érkezik hozzánk, tanulmányaik alatt a magyar hallgatók állami ösztöndíjának megfelelő javadalmazásban és lakhatási támogatásban részesülnek. Az első, ebben a 
                konstrukcióban részt vevő hallgatók az utóbbi években végeztek, közülük többen fokozatot is szereztek.
            </div>
            <div lang="en">
                The Tempus Foundation's Stipendium Hungaricum programme is an important step towards the internationalisation of our doctoral schools. A large proportion of doctoral students applying for English courses are from the Middle East, Africa and China. During their studies, they receive a scholarship and housing allowance equivalent to that of Hungarian students. The first students participating in this programme graduated in recent years, many of them earned the degree.
            </div>
        </ChartBlock>
        <ChartBlock lang={props.lang} chart="phd_entering">
            <div className='section-title' lang="hu">Felvett doktoranduszok száma</div>
            <div className='section-title' lang="en">PhD students entering the program</div>
            <div lang="hu">
                A doktori képzés népszerűségéről a belépő, frissen felvett hallgatók száma adja a legjobb képet.
            </div>
            <div lang="en">
                The best indicator of the popularity of doctoral training is the number of new students entering the programme.
            </div>
        </ChartBlock>
        <ChartBlock lang={props.lang} chart="phd_diss">
            <div className='section-title' lang="hu">Doktori fokozatszerzések</div>
            <div className='section-title' lang="en">Number of PhD titles earned</div>
            <div lang="hu">
                A doktori képzés sikerességének legfontosabb mutatója természetesen a sikeres fokozatszerzések száma. A korábbi három éves képzés erre nézve semmilyen időbeli korlátot nem írt elő, szemben a négy éves képzéssel, 
                melyben a komplex vizsgától számított három éven belül be kell nyújtani a disszertációt, mely komoly motivációt jelent a doktori képzés lezárására.
            </div>
            <div lang="en">
                The most important indicator of the success of doctoral training is, of course, the number of doctoral degrees earned. The previous three-year programme did not impose any time limit for this, unlike the recently introduced four-year programme, which requires the submission of a dissertation within three years after the complex examination, which is a strong motivation to PhD students to complete their doctoral studies.
            </div>            
        </ChartBlock>
    </div>;
}

function TopResults(props) {

    return <div className="chart-block-container">
        <TopList lang={props.lang} label={{hu: "Legmagasabb impakt faktorú cikkeink", en: "Papers with top impact factor"}} file={"top_if_ever.json"} />
        <TopList lang={props.lang} label={{hu: "Legidézettebb publikációink", en: "Most cited publications"}} file={"top_citations_ever.json"} />
        <TopList lang={props.lang} label={{hu: "Legnagyobb hatású publikációk", en: "Most influential publications"}} file={"top_citations_incites.json"} />
        <TopList lang={props.lang} label={{hu: "Legmagasabb H indexű kutatóink", en: "Researchers with top H index"}} file={"top_h_index.json"} />
    </div>;
}

function TopList(props) {

    const [topData, setTopData] = useState();
    useEffect(() => {
        fetch(`./results/${props.file}`)
            .then(res => res.json())
            .then(top => setTopData(top));
    }, [props]);

    return <div className="chart-block">
        <div className='section-title'>{props.label[props.lang]}</div>
        {topData && <>
            <div>{topData.description[props.lang]}</div>
            <div className="top-result-list">
                {topData.toplist.map((d, ix) => <a href={d.link} key={ix} className="top-result-item">
                        <div className="top-result-item-rank-container"><div className="top-result-item-rank">{d.rank}</div></div>
                        <div className="top-result-item-decoration"></div>
                        <div className="top-result-item-body">
                            {d.label && <div className="top-result-item-label">{d.label[props.lang]}</div>}
                            <ReactMarkdown>{d.text}</ReactMarkdown>
                        </div>
                    </a>)}
            </div>
        </>}
    </div>;
}


function ChartBlock(props) {

    const [chartData, setChartData] = useState();

    useEffect(() => {
        fetch(`./results/${props.chart}_${props.lang}.json`)
            .then(res => res.json())
            .then(chrt => setChartData(chrt));
    }, [props]);

    return <div className="chart-block">
        {Children.toArray(props.children).filter((child, i) => child.props.lang===props.lang)}
        {chartData && <>
            <div className="chart-container">
                    <Chart type={chartData.type} options={chartData.options} data={chartData.data} />
            </div>
            <div className="chart-explanation">{chartData.explanation}</div>
        </>}
    </div>;
}